import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PhotoUploader from "../components/PhotoUploader";
import ConditionChecklist from "../components/ConditionChecklist";
import { listingsService } from "../services/listingsService";

export default function EditListing() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("Clothing");
  const [condition, setCondition] = useState("good");
  const [photos, setPhotos] = useState([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadListing = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      setLoading(true);
      setError("");
      try {
        const listing = await listingsService.getListing(id);
        
        // Check if user owns this listing
        if (listing.user_id !== user.id) {
          setError("You don't have permission to edit this listing");
          return;
        }

        // Populate form with existing data
        setTitle(listing.title || "");
        setPrice(listing.price_cents ? (listing.price_cents / 100).toString() : "");
        setCategory(listing.category || "Clothing");
        setCondition(listing.condition || "good");
        setPhotos(listing.photos || []);
        setDescription(listing.description || "");
      } catch (err) {
        console.error('Error loading listing:', err);
        setError("Failed to load listing");
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      loadListing();
    }
  }, [id, user, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!user) {
      setError("You must be logged in to edit a listing");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const listingData = {
        title,
        price_cents: Math.round(Number(price || 0) * 100),
        category,
        condition,
        description,
        photos
      };

      await listingsService.updateListing(id, listingData);
      navigate(`/listing/${id}`);
    } catch (err) {
      console.error('Error updating listing:', err);
      setError(err.message || "Failed to update listing");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-3xl">👶</span>
          </div>
          <p className="text-slate-600">Loading listing...</p>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-3xl">⚠</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Error</h3>
          <p className="text-slate-600 mb-6">{error}</p>
          <button 
            className="btn-primary"
            onClick={() => navigate('/my-listings')}
          >
            Back to My Listings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-center mx-auto max-w-2xl">
      {/* Page Header */}
      <div className="mb-8 text-center">
        <h1 className="text-responsive-xl font-bold text-slate-900 mb-2">
          Edit Listing
        </h1>
        <p className="text-responsive-base text-slate-600">
          Update your baby item listing
        </p>
      </div>

      {/* Form Container */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl border border-white/40 shadow-xl p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="form-label">Item Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="form-input"
              placeholder="e.g., Bundle of newborn onesies"
              required
            />
          </div>

          {/* Price and Category Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="form-label">Price (CAD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 font-semibold">$</span>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="form-input pl-10"
                  inputMode="decimal"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="form-label">Category</label>
              <select 
                value={category} 
                onChange={(e) => setCategory(e.target.value)} 
                className="form-select"
              >
                <option value="Clothing">👕 Clothing</option>
                <option value="Gear">🚗 Gear</option>
                <option value="Toys">🧸 Toys</option>
                <option value="Feeding">🍼 Feeding</option>
                <option value="Nursery">🛏️ Nursery</option>
                <option value="Other">📦 Other</option>
              </select>
            </div>
          </div>

          {/* Condition */}
          <div className="space-y-3">
            <label className="form-label">Condition</label>
            <ConditionChecklist value={condition} onChange={setCondition} />
          </div>

          {/* Photos */}
          <div className="space-y-3">
            <label className="form-label">Photos</label>
            <PhotoUploader value={photos} onChange={setPhotos} />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="form-label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-input resize-none"
              rows={4}
              placeholder="Any details buyers should know (manufacture date for car seats, stains, etc.)"
            />
            <p className="text-xs text-slate-500">
              Include details like size, brand, condition notes, and any special features.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs">!</span>
                </div>
                <div className="text-sm text-red-800">{error}</div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button 
              type="button"
              className="flex-1 btn-secondary text-lg py-3"
              onClick={() => navigate('/my-listings')}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="flex-1 btn-primary text-lg py-3 disabled:opacity-60"
              disabled={saving}
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
